import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";
import { Container } from "pixi.js";

import { IReelData } from "../../../abstracts/elements/AbstractReel";
import { EventsManager } from "../../../abstracts/events/EventsManager";
import { AbstractBitmapText } from "../../../abstracts/graphics/AbstractBitmapText";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { IScatterDecoratorData } from "../../../abstracts/types/interfaces";
import { flat, isAllTrue, shuffleArray } from "../../../abstracts/utils/ArrayUtils";
import { generateScaledSprite } from "../../../abstracts/utils/GraphicUtils";
import { fadeAndStopSound, playSound } from "../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../Apocalypse";
import {
    ApocalypseFontsNames,
    ApocalypseSoundNames,
} from "../../assetsLoader/ApocalypseAssetsNames";
import {
    fsConfig,
    getWinLevel,
    reelsConfig,
    symbolConfig,
    winLevels,
} from "../../config/ApocalypseConfig";
import { SHOW_EASTER_EGG_ANIMATION } from "../../events/ApocalypseEvents";
import { ApocalypseFsContainer } from "../../modules/Reels/fs/ApocalypseFsContainer";
import ApocalypseReel from "../../modules/Reels/normal/ApocalypseReel";
import { ApocalypseBlankSymbol } from "../../modules/Reels/symbols/ApocalypseBlankSymbol";
import { ApocalypseLowSymbolNames } from "../../types/ApocalypseEnums";

export const ApocalypseFsFirstStageDecorator = (data: IScatterDecoratorData): void => {
    const {
        finalDataReels = [],
        initialDataReels = [],
        gameReels = [],
        timeline = gsap.timeline(),
        stopSpinDelay = 0,
        onDecoratorComplete = () => {},
        reelsContainer = new Container(),
    } = data;

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_START, `>`);

    let currentTimelinePosition: number = timeline.labels[TimelineLabels.SCATTER_DECORATOR_START];
    const flatEvaluatedData = flat(Object.assign([], finalDataReels));
    const flatInitialData = flat(Object.assign([], initialDataReels));
    const fsContainer = reelsContainer as ApocalypseFsContainer;
    const stoppedReels: boolean[] = new Array(finalDataReels.length).fill(false);
    const reelsLanded: boolean[] = new Array(finalDataReels.length).fill(false);

    shuffleArray(fsContainer.lowLockerSoundNames);
    fsContainer.lowLockerCurrentSoundIndex = 0;

    const onReelSpinComplete = (reelId: number): void => {
        const checkIfAllReelsStopped = () => {
            stoppedReels[reelId] = true;

            if (isAllTrue(stoppedReels)) {
                if (Apocalypse.model.lostRounds === 10) {
                    Apocalypse.model.lostRounds = 0;
                    EventsManager.trigger(SHOW_EASTER_EGG_ANIMATION);
                }

                const completeTl = gsap.timeline({
                    onComplete: () => {
                        gsap.delayedCall(0.1, onDecoratorComplete);
                    },
                    delay: 0.1,
                });

                const multiplyTl = gsap.timeline();
                let totalWinValue = fsContainer.fsMultiplierContainer.counter.valueObj.value;
                let winDiff = fsContainer.fsMultiplierContainer.counter.valueObj.value;
                let winningReelsCounter: number = 0;

                flatEvaluatedData.forEach((reel, reelIndex) => {
                    if (flatInitialData[reelIndex] !== reel) {
                        const targetSymbol = (
                            gameReels[reelIndex] as ApocalypseReel
                        ).getAllVisibleStopSymbols()[0] as ApocalypseBlankSymbol;

                        totalWinValue += targetSymbol.valueObj.value;

                        const globalPosition = targetSymbol.getGlobalPosition();

                        multiplyTl.add(
                            fsContainer.fsMultiplierContainer.getDefaultMultiplierAnimation(
                                globalPosition,
                                targetSymbol.valueObj.value,
                                totalWinValue,
                                true
                            ),
                            winningReelsCounter * 0.2
                        );

                        winningReelsCounter++;
                    }
                });

                if (winningReelsCounter > 0) {
                    const totalDuration = multiplyTl.totalDuration();
                    winDiff = totalWinValue - winDiff;

                    const winLevel = getWinLevel(winDiff);
                    const maxWinLevel = winLevels[9];

                    if (totalWinValue > maxWinLevel.min) {
                        totalWinValue = maxWinLevel.min;
                    }

                    const winLevelAnimationDuration = winLevel.animationDuration / 6;

                    let duration = Math.max(Math.min(winLevelAnimationDuration, 1), totalDuration);
                    if (duration > 0.35) {
                        duration -= 0.35;
                    }

                    multiplyTl.add(
                        fsContainer.fsMultiplierContainer.counter.getUpdateCounterTextAnimation(
                            totalWinValue,
                            false,
                            true,
                            duration
                        ),
                        0.6
                    );

                    completeTl.add(multiplyTl, 0);
                }
            }
        };

        if (
            Object.values(ApocalypseLowSymbolNames).includes(
                finalDataReels[reelId][0] as ApocalypseLowSymbolNames
            )
        ) {
            // Do stuff here

            const lowLockAnimation = (
                reelsContainer as ApocalypseFsContainer
            ).getLockLowReelAnimation(reelId, checkIfAllReelsStopped);

            if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                lowLockAnimation.timeScale(2);
            }
        } else {
            checkIfAllReelsStopped();
        }
    };

    let reelStopTimeline: gsap.core.Timeline = gsap.timeline();

    const onReelLand = (reelId: number): void => {
        reelsLanded[reelId] = true;
        if (isAllTrue(reelsLanded)) {
            fadeAndStopSound({
                name: ApocalypseSoundNames.REELS_SPIN,
                from: 1,
                to: 0,
                duration: 0.1,
            });
        }
        reelStopTimeline.resume();
    };

    gameReels.forEach((reel, reelIndex) => {
        let newStopDelay =
            stopSpinDelay === fsConfig.stopSpinReelDelay ? stopSpinDelay + 0.05 : stopSpinDelay / 2;

        const data: IReelData = {
            stopSymbolNames: finalDataReels[reelIndex],
        };
        const reelLabel: string = `reelBeginStop${reelIndex}`;

        timeline.addLabel(reelLabel, currentTimelinePosition);

        timeline.add(
            (reel as ApocalypseReel).stopNormalBetAnimation(data, onReelSpinComplete, onReelLand),
            currentTimelinePosition
        );

        if (reel.isLocked) {
            newStopDelay = 0;
        }

        currentTimelinePosition += newStopDelay;

        timeline.addLabel(`reelStopped${reelIndex}`);
    });

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_COMPLETE);
};

export const addExtraSpin = (
    fsContainer: ApocalypseFsContainer,
    reelIndex: number,
    finalCounterValue: number,
    isFirstStage: boolean = false
): gsap.core.Timeline => {
    const extraSpinWonFrame = generateScaledSprite(`apocalypse/extraSpinWonFrame`);
    extraSpinWonFrame.x = -6 + reelIndex * (symbolConfig.symbolWidth + reelsConfig.reelSpacing);
    extraSpinWonFrame.y = -4;
    extraSpinWonFrame.alpha = 0;
    fsContainer.addChildAt(extraSpinWonFrame, 4);

    const addExtraSpinTl = gsap.timeline({
        onStart: () => {
            playSound({ name: ApocalypseSoundNames.FS_SPIN_WON_GREEN_FRAME });
        },
    });

    const showReelFilledFrame = gsap.to(extraSpinWonFrame, {
        alpha: 1,
        duration: 0.6,
    });

    addExtraSpinTl.add(showReelFilledFrame, 0);

    const plusOneText = new AbstractBitmapText({
        text: `+1`,
        style: {
            fontName: ApocalypseFontsNames.APOCALYPSE_GREEN,
            fontSize: 9,
        },
    });
    plusOneText.name = `plusOneText`;

    plusOneText.x = extraSpinWonFrame.x + extraSpinWonFrame.width / 2;
    plusOneText.y = extraSpinWonFrame.y + extraSpinWonFrame.height - symbolConfig.symbolHeight;
    plusOneText.alpha = 0;
    fsContainer.addChild(plusOneText);

    const showPlusOneText = gsap.to(plusOneText, {
        alpha: 1,
        y: 49,
        duration: 0.6,
    });

    addExtraSpinTl.add(showPlusOneText, 0);

    const hideFrame = gsap.to(extraSpinWonFrame, {
        alpha: 0,
        duration: 0.4,
        onComplete: () => {
            extraSpinWonFrame.destroy();
        },
    });

    addExtraSpinTl.add(hideFrame, `>`);

    const movePlusOneText = gsap.to(plusOneText, {
        x: -67,
        y: isFirstStage ? -54 : 20,
        duration: 0.4,
    });

    addExtraSpinTl.add(movePlusOneText, `<`);

    const hidePlusOneText = gsap.to(plusOneText, {
        alpha: 0,
        duration: 0.1,
        onComplete: () => {
            plusOneText.destroy();

            playSound({ name: ApocalypseSoundNames.FS_SPIN_WON_COUNTUP });
        },
    });

    addExtraSpinTl.add(hidePlusOneText, `>`);

    const fsLeftCounter = fsContainer.fsLeftContainer.counter;
    // const totalFsLeft = fsLeftCounter.valueObj.value + 1;
    addExtraSpinTl.add(fsLeftCounter.getUpdateCounterTextAnimation(finalCounterValue), `<`);
    // addExtraSpinTl.call(() => {
    //     this.keypad.setZeroBetSpinCounter(finalCounterValue);
    // });

    return addExtraSpinTl;
};
